export type RegressionContractMode = 'json-dev' | 'normalized';

export type RegressionWarningCode = 'json-missing' | 'json-invalid' | 'json-partial';

export enum RegressionFileQuantityType {
	Single = 1,
	Multiple = 2,
}

export enum RegressionAssertType {
	Equals = 'Equals',
	True = 'True',
	Contains = 'Contains',
}

export type RegressionAssertProperty =
	| 'CompanyId'
	| 'DestinationId'
	| 'OriginalFileName'
	| 'OutboundFileName'
	| 'PlanId'
	| 'StateId';

export interface RegressionWarning {
	code: RegressionWarningCode;
	message: string;
}

export interface RegressionFileMetadata {
	originalName: string;
	displayName: string;
	blobPath: string;
	file?: File;
}

export interface RegressionAssert {
	assertId: number;
	property: RegressionAssertProperty;
	assertType: RegressionAssertType;
	value: string;
}

export interface RegressionReplacementDate {
	dateToBeReplaced: string;
	dateFormat: string;
}

export interface RegressionTestFormValues {
	testName: string;
	description: string;
	contractNumber: string;
	testTypeId: number;
	replacementDates: RegressionReplacementDate[];
	isEnabled: boolean;
	shouldRun: boolean;
	payrollCompany: string;
	investmentCompanyName: string;
	investmentCompanyMasterId: number | null;
	testedFeatureIds: number[];
	asserts: RegressionAssert[];
	databaseNumber: string;
	inputFiles: RegressionFileMetadata[];
	baselineFiles: RegressionFileMetadata[];
}

export interface RegressionTransportMetadata {
	transportMode: RegressionContractMode;
	dateCreated?: string | null;
	dateModified?: string | null;
	lastModifiedBy?: string | null;
	rawJsonData?: string | null;
	isPartiallyMalformed: boolean;
	warnings: RegressionWarning[];
}

export interface RegressionTestRecord extends RegressionTestFormValues {
	testId: number;
	meta: RegressionTransportMetadata;
}

export interface RegressionTestNormalizedDto extends RegressionTestFormValues {
	testId: number;
	dateCreated?: string | null;
	dateModified?: string | null;
	lastModifiedBy?: string | null;
}

export interface RegressionTestNormalizedRequest extends RegressionTestFormValues {
	testId?: number;
}

export interface RegressionTestTypeOption {
	testTypeId: number;
	testTypeName: string;
	inputFileQuantityTypeId: RegressionFileQuantityType;
	outputFileQuantityTypeId: RegressionFileQuantityType;
}

export interface RegressionTestableFeature {
	testableFeatureId: number;
	name: string;
}

export interface RegressionInvestmentCompanyOption {
	investmentCompanyMasterId: number;
	companyName: string;
	defaultPayrollDateFormat: string | null;
}

export interface RegressionSessionStateOption {
	stateId: number;
	stateDescription: string;
}

export const defaultRegressionDateFormat = 'MM/dd/yyyy';

export const defaultRegressionReplacementDate: RegressionReplacementDate = {
	dateToBeReplaced: '12/31/1999',
	dateFormat: defaultRegressionDateFormat,
};

export const defaultRegressionAssertTemplates: ReadonlyArray<Omit<RegressionAssert, 'assertId'>> = [
	{
		property: 'CompanyId',
		assertType: RegressionAssertType.Equals,
		value: '',
	},
	{
		property: 'PlanId',
		assertType: RegressionAssertType.Equals,
		value: '',
	},
	{
		property: 'StateId',
		assertType: RegressionAssertType.Equals,
		value: '2',
	},
];

export const emptyRegressionTestFormValues: RegressionTestFormValues = {
	testName: '',
	description: '',
	contractNumber: '',
	testTypeId: 1,
	replacementDates: [],
	isEnabled: false,
	shouldRun: false,
	payrollCompany: 'iSolved',
	investmentCompanyName: '',
	investmentCompanyMasterId: null,
	testedFeatureIds: [1],
	asserts: [],
	databaseNumber: '504',
	inputFiles: [],
	baselineFiles: [],
};
